// Maps controls and frameworks to the assessment page where they can be reviewed.
// Shared by every surface that links out from a control ID (compliance check results,
// continuous guidance, auditor findings, dashboard) so nothing falls back to an
// ISO 27001 page for an organisation that never enrolled in ISO 27001.

/** Landing page for each framework's assessment. */
export const FRAMEWORK_ROUTES: Record<string, string> = {
  iso27001:              "/mandatory-clauses",
  cyber_essentials:      "/cyber-essentials",
  cyber_essentials_plus: "/cyber-essentials-plus",
  iso42001:              "/iso42001",
  iso9001:               "/iso9001",
  soc2:                  "/soc2",
  pci_dss:               "/pci-dss/saq-a",
  iasme_ca_sole_trader:  "/iasme-ca-sole-trader",
  iasme_ca_micro:        "/iasme-ca-micro",
  iasme_ca_small:        "/iasme-ca-small",
  iasme_ca_medium_large: "/iasme-ca-medium-large",
  nist_csf:              "/nist-csf",
  gdpr:                  "/gdpr",
  uk_gdpr:               "/gdpr",
  iso45001:              "/iso45001",
  iso14001:              "/iso14001",
  iwa48:                 "/iwa48",
  ncsc_caf:              "/ncsc-caf",
  legal:                 "/legal",
};

const CE_THEME_IDS = new Set(["firewalls", "secure_config", "user_access", "malware_protection", "patch_management"]);

// Phrases the AI uses when a summary sentence is about a particular standard. Order
// matters — more specific keywords must appear before overlapping ones, and "Annex A"
// is deliberately not an ISO 27001 keyword because ISO 42001 also has an Annex A.
export const FRAMEWORK_KEYWORDS: Array<{ id: string; keywords: string[] }> = [
  { id: "cyber_essentials_plus", keywords: ["Cyber Essentials Plus", "CE+"] },
  { id: "cyber_essentials",      keywords: ["Cyber Essentials"] },
  { id: "iso27001",              keywords: ["ISO 27001", "ISO/IEC 27001", "27001", "ISMS"] },
  { id: "iso9001",               keywords: ["ISO 9001", "Quality Management System", "QMS"] },
  { id: "iso42001",              keywords: ["ISO 42001", "ISO/IEC 42001", "AI Management System", "AIMS"] },
  { id: "iso45001",              keywords: ["ISO 45001"] },
  { id: "iso14001",              keywords: ["ISO 14001"] },
  { id: "ncsc_caf",              keywords: ["NCSC CAF", "Cyber Assessment Framework", "CAF v4"] },
  { id: "soc2",                  keywords: ["SOC 2", "SOC2", "Trust Services Criteria", "Trust Services"] },
  { id: "pci_dss",               keywords: ["PCI DSS", "PCI"] },
  { id: "iasme_ca_sole_trader",  keywords: ["IASME CA (Sole Trader)", "IASME Cyber Assurance — Sole Trader"] },
  { id: "iasme_ca_micro",        keywords: ["IASME CA (Micro)", "IASME Cyber Assurance — Micro"] },
  { id: "iasme_ca_small",        keywords: ["IASME CA (Small)", "IASME Cyber Assurance — Small"] },
  { id: "iasme_ca_medium_large", keywords: ["IASME CA (Medium/Large)", "IASME Cyber Assurance — Medium/Large"] },
];

/** Framework a summary sentence is about, judged by the keywords above; null if none. */
export function frameworkOfSentence(sentence: string): string | null {
  for (const { id, keywords } of FRAMEWORK_KEYWORDS) {
    if (keywords.some((kw) => sentence.includes(kw))) return id;
  }
  return null;
}

/**
 * Drops sentences that are clearly about a standard outside the run's scope. Runs
 * stored before per-framework scoping joined every standard's narrative into one
 * string, so a Cyber Essentials-only org could otherwise be told about ISO 27001.
 */
export function scopeSummaryText(summary: string, frameworkInScope: (fw: string) => boolean): string {
  return summary
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => {
      const fw = frameworkOfSentence(sentence);
      return fw === null || frameworkInScope(fw);
    })
    .join(" ")
    .trim();
}

/**
 * Infers the framework a control ID belongs to from the seeded ID conventions.
 * Bare IDs ("4.1", "A.5.1") are ISO 27001 — the only framework seeded without a prefix.
 * Prefer the `frameworkId` stored on a result entry when one exists; this is the
 * fallback for runs and findings recorded before that field was introduced.
 */
export function frameworkOfControl(controlId: string): string | null {
  if (controlId.startsWith("iso42001-")) return "iso42001";
  if (controlId.startsWith("iso9001-")) return "iso9001";
  if (controlId.startsWith("iso45001-")) return "iso45001";
  if (controlId.startsWith("iso14001-")) return "iso14001";
  if (controlId.startsWith("ncsc-caf-")) return "ncsc_caf";
  if (controlId.startsWith("soc2-")) return "soc2";
  if (controlId.startsWith("pci-")) return "pci_dss";
  if (controlId.startsWith("iasme-ca-sole-trader-")) return "iasme_ca_sole_trader";
  if (controlId.startsWith("iasme-ca-micro-")) return "iasme_ca_micro";
  if (controlId.startsWith("iasme-ca-small-")) return "iasme_ca_small";
  if (controlId.startsWith("iasme-ca-medium-large-")) return "iasme_ca_medium_large";
  if (controlId.startsWith("legal-")) return "legal";
  if (/^(ce-|fw_|sc_|um_|ua_|mp_)/.test(controlId) || CE_THEME_IDS.has(controlId)) return "cyber_essentials";
  if (controlId.startsWith("iso27001-") || /^A\.\d/.test(controlId) || /^\d+(\.\d+)*$/.test(controlId)) return "iso27001";
  return null;
}

/**
 * Page where a control can be reviewed and actioned. Uses the recorded framework when
 * given, otherwise infers it from the ID. Unknown controls go to the dashboard rather
 * than to any one standard's page.
 */
export function controlRoute(controlId: string, frameworkId?: string | null): string {
  const fw = frameworkId || frameworkOfControl(controlId);
  if (fw === "iso27001") return /^(iso27001-)?A\./.test(controlId) ? "/annex-a" : "/mandatory-clauses";
  if (fw === "iso42001") return /^iso42001-[BC]\./.test(controlId) ? "/iso42002" : "/iso42001";
  return FRAMEWORK_ROUTES[fw ?? ""] ?? "/dashboard";
}
